/**
 * THE PARAMETER SCHEMA — what a node kind's knobs ARE, declared as data beside the generator that
 * reads them. See docs/node-graph-refactor-plan.md §2.4.
 *
 * This exists because the world dialog was 557 lines of hand-authored form: twelve `Field<T>` arrays,
 * 74 sliders and 11 pills, listing per subsystem what the generator already knew about itself. It
 * reached 74 of the 116 tunable fields, and every gate between them was written twice — once as a
 * pill's `disabled`, once as the `&&` chain in front of the matching section. Moving the schema onto
 * the kind makes the editor a RENDERER rather than a form, so adding a knob is one line here and the
 * UI grows for free.
 *
 * SEVEN VALUE SHAPES, and the count is forced rather than chosen. The live config already holds
 * numbers, booleans, an enum (the height model), a weighted list (region type weights), a set
 * (which regions scatter), material ids, a list of records (the strata, the stamp distributions) and
 * a draggable spline (the elevation curve). A schema that describes only numbers describes 74 of the
 * dialog's controls and none of the other seven.
 *
 * `enabledWhen` GREYS, IT NEVER HIDES. A control that vanishes is a control the user hunts for — the
 * current dialog gets this right with `disabled` + a `title` explaining why, and the renderer must
 * keep it. Same for a disabled node's children: greyed and collapsed, never gone.
 *
 * THERE IS NO `tier`. Parameters used to split into basic and advanced behind a per-node
 * disclosure, which is a second collapsed thing inside an already-collapsed node — the tree itself
 * is the progressive disclosure now, so a node shows all of its own knobs and the formerly-advanced
 * ones simply sit at the end of the list.
 */
/** The default value of any spec, without a per-shape switch at every call site. */
export function paramDefault(p) {
    return p.def;
}
/** True when the value is of the shape the spec declares. Used by `parse` and by the guard. */
export function paramValid(p, v) {
    switch (p.t) {
        case 'number': return typeof v === 'number' && Number.isFinite(v);
        case 'bool': return typeof v === 'boolean';
        case 'enum': return typeof v === 'string' && p.options.some((o) => o.value === v);
        case 'multi': return Array.isArray(v) && v.every((s) => p.options.some((o) => o.value === s));
        case 'weights': return Array.isArray(v) && v.length === p.of.length && v.every((n) => typeof n === 'number');
        case 'material': return typeof v === 'string';
        case 'curve': return Array.isArray(v) && v.every((q) => q && typeof q.x === 'number' && typeof q.y === 'number');
        case 'list': return Array.isArray(v);
    }
}
/**
 * Clamp a number to its spec. NOT applied on read — a stored value outside the slider's range is
 * legitimate (the range is what the UI offers, not what the generator accepts), and silently moving
 * it would change a world's terrain because its author dragged a slider somewhere else. Offered for
 * the editor to use on input.
 */
export function clampParam(p, v) {
    return p.t === 'number' ? Math.min(p.max, Math.max(p.min, v)) : v;
}
//# sourceMappingURL=params.js.map